import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  cancelRecurring,
  rescheduleRecurring,
  restoreRecurring,
  skipRecurring,
  type RecurringItem,
} from '@/api/recurring'
import { getLoanDetail } from '@/api/loans'
import { DatePicker } from '@/components/DatePicker'
import { LoanPaymentModal } from '@/components/loans/LoanModals'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatDate } from '@/lib/formatDate'
import { ConfirmBox } from './ConfirmBox'
import { MarkDoneSheet } from './MarkDoneSheet'
import { undoLabel as stepLabel, useUndoRecurring } from './useUndoRecurring'

type Dialog = 'done' | 'emi' | 'missed' | 'reschedule' | 'cancel' | null

function errorDetail(err: unknown): string | undefined {
  return (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
}

export type RecurringActions = ReturnType<typeof useRecurringActions>

/** Every action on a reminder, shared by the strip card, the row menu and the detail view. */
export function useRecurringActions(
  item: RecurringItem,
  opts: { onEdit?: (item: RecurringItem) => void; onCancelled?: () => void } = {}
) {
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<Dialog>(null)
  const [reason, setReason] = useState('')
  const [newDueDate, setNewDueDate] = useState(item.nextDueDate)
  const isLinkedEmi = !!item.loanAccountId
  const lastStep = item.undoable?.[0]
  const undoMutation = useUndoRecurring(item.id)

  const { data: loan } = useQuery({
    queryKey: ['loan', item.loanAccountId],
    queryFn: () => getLoanDetail(item.loanAccountId!),
    enabled: isLinkedEmi && dialog === 'emi',
  })

  const doneWithUndo = (message: string) => {
    queryClient.invalidateQueries({ queryKey: ['recurring'] })
    queryClient.invalidateQueries({ queryKey: ['calendar'] })
    toast.success(message, { action: { label: 'Undo', onClick: () => undoMutation.mutate() } })
  }

  const skipMutation = useMutation({
    mutationFn: () => skipRecurring(item.id, reason),
    onSuccess: () => {
      setDialog(null)
      setReason('')
      doneWithUndo(`${item.name} marked missed`)
    },
    onError: () => toast.error('Failed to mark as missed. Please try again.'),
  })

  const rescheduleMutation = useMutation({
    mutationFn: () => rescheduleRecurring(item.id, newDueDate),
    onSuccess: () => {
      setDialog(null)
      doneWithUndo(`Next due date set to ${formatDate(newDueDate)}`)
    },
    onError: (err) =>
      toast.error(errorDetail(err) ?? 'Failed to change the due date. Please try again.'),
  })

  const cancelMutation = useMutation({
    mutationFn: () => cancelRecurring(item.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring'] })
      setDialog(null)
      opts.onCancelled?.()
    },
    onError: () => toast.error('Failed to cancel recurring item. Please try again.'),
  })

  const restoreMutation = useMutation({
    mutationFn: () => restoreRecurring(item.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring'] }),
    onError: () => toast.error('Failed to restore recurring item. Please try again.'),
  })

  // Portalled so a transformed ancestor (the detail panel) can't trap these fixed overlays.
  // React still bubbles their events to the opener, so stop clicks and Esc here.
  const dialogs = createPortal(
    <div
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape' && dialog !== 'emi') setDialog(null)
      }}
    >
      {dialog === 'done' && (
        <MarkDoneSheet
          item={item}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            setDialog(null)
            doneWithUndo(message)
          }}
        />
      )}

      {dialog === 'missed' && (
        <ConfirmBox title="Mark as Missed">
          <p className="text-sm text-[var(--fl-text-2)] mb-3">
            {item.name} · due {formatDate(item.nextDueDate)}. Nothing is recorded; the next due date
            moves on.
          </p>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            className="mb-3 px-2.5 py-1.5"
          />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDialog(null)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => skipMutation.mutate()}
              disabled={skipMutation.isPending}
              className="flex-1 font-semibold"
            >
              Confirm Missed
            </Button>
          </div>
        </ConfirmBox>
      )}

      {dialog === 'reschedule' && (
        <ConfirmBox title="Change next due date">
          <p className="text-sm text-[var(--fl-text-2)] mb-4">
            {item.name} is due {formatDate(item.nextDueDate)}. Pick an earlier date to record a
            cycle again, or a later one to push it out.
          </p>
          <DatePicker value={newDueDate} onChange={setNewDueDate} />
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setDialog(null)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => rescheduleMutation.mutate()}
              disabled={
                !newDueDate || newDueDate === item.nextDueDate || rescheduleMutation.isPending
              }
              className="flex-1 font-semibold"
            >
              Save
            </Button>
          </div>
        </ConfirmBox>
      )}

      {dialog === 'cancel' && (
        <ConfirmBox title={`Cancel "${item.name}"?`}>
          <p className="text-sm text-[var(--fl-text-2)] mb-4">
            It will move to your Cancelled tab.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDialog(null)} className="flex-1">
              Keep
            </Button>
            <Button
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="flex-1 font-semibold"
            >
              Cancel item
            </Button>
          </div>
        </ConfirmBox>
      )}

      {dialog === 'emi' && loan && (
        <LoanPaymentModal
          loan={loan}
          type="EMI"
          recurring={{
            itemId: item.id,
            dueDate: item.nextDueDate,
            accountId: item.financialAccountId ?? undefined,
            emiAmount: item.amount,
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>,
    document.body
  )

  return {
    markPaid: () => setDialog(isLinkedEmi ? 'emi' : 'done'),
    openMissed: () => {
      setReason('')
      setDialog('missed')
    },
    openReschedule: () => {
      setNewDueDate(item.nextDueDate)
      setDialog('reschedule')
    },
    undoLast: lastStep ? () => undoMutation.mutate() : null,
    undoLabel: lastStep ? stepLabel(item, lastStep) : null,
    edit: () => opts.onEdit?.(item),
    askCancel: () => setDialog('cancel'),
    restore: () => restoreMutation.mutate(),
    pending: restoreMutation.isPending,
    dialogs,
  }
}
